import React, { useEffect, useState } from "react";
import TrustRequestCard from "./TrustRequestCard";
import { useTrustedNetwork } from "../../../../context/TrustedNetworkContext";
import TrustNetworkConfigDialog from "../TrustNetworkConfig/TrustNetworkConfigDialog";
import { Dialog, Loader, Button, Flex, Text } from "@fluentui/react-northstar";
import { Empty, Divider, Input } from "antd";
import { InboxOutlined, TeamOutlined, SendOutlined, SearchOutlined } from "@ant-design/icons";
import { api } from "../../../../Assets/api/api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import "./styles.scss";

const RequestSection = ({
  title,
  description,
  requests,
  onOpenConfig,
  onApprove,
  onReject,
  showActions,
  isGroupRequest = false,
  isApprovingRequest = {},
  isRejectingRequest = {},
  onRequestDeleted,
  isMobile,
  isMediumScreen,
  sectionIcon,
}) => {
  if (requests.length === 0) {
    return (
      <div className="request-section">
        <div className="section-title">
          {sectionIcon}
          {title}
        </div>
        {description && (
          <div className="section-description">{description}</div>
        )}
        <Divider className="section-divider" />
        <div className="empty-state">
          <InboxOutlined className="empty-icon" />
          <div className="empty-text">No {title.toLowerCase()}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="request-section">
      <div className="section-title">
        {sectionIcon}
        {title}
      </div>
      {description && <div className="section-description">{description}</div>}
      <Divider className="section-divider" />
      <div className="requests-grid">
        {requests.map((request) => (
          <TrustRequestCard
            key={request.id}
            trustRequest={request}
            onOpenConfig={() => onOpenConfig(request)}
            onApprove={() => onApprove(request.id)}
            onReject={() => onReject(request.id)}
            showActions={showActions}
            isGroupRequest={isGroupRequest}
            isApprovingRequest={isApprovingRequest}
            isRejectingRequest={isRejectingRequest}
            onRequestDeleted={onRequestDeleted}
            isMobile={isMobile}
            isMediumScreen={isMediumScreen}
          />
        ))}
      </div>
    </div>
  );
};

const TrustRequests = () => {
  const {
    getTrustRequests,
    isLoadingRequests,
    receivedRequests,
    createdRequests,
    approveTrustRequest,
    rejectTrustRequest,
    user,
  } = useTrustedNetwork();
  const { t } = useTranslation();
  const translation = t("trustedNetwork").trustRequests;

  // Responsive state
  const [isMobile, setIsMobile] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [config, setConfig] = useState({
    shareCollection: false,
    shareSpinWheel: false,
    customSettings: null,
  });
  const [requestConfigs, setRequestConfigs] = useState({});
  const [groupRequests, setGroupRequests] = useState([]);
  const [isLoadingGroupRequests, setIsLoadingGroupRequests] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null, // 'approve' or 'reject'
    requestId: null,
    isGroupRequest: false,
    isLoading: false, // Add loading state for confirmation dialog
  });

  const [isApprovingGroupRequest, setIsApprovingGroupRequest] = useState({});
  const [isRejectingGroupRequest, setIsRejectingGroupRequest] = useState({});
  const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);

  // Search functionality
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredReceivedRequests, setFilteredReceivedRequests] = useState([]);
  const [filteredCreatedRequests, setFilteredCreatedRequests] = useState([]);
  const [filteredGroupRequests, setFilteredGroupRequests] = useState([]);

  // Responsive detection
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsMediumScreen(width > 768 && width <= 1100); // Reduce from 1200 to 1100 to catch the cropping issue
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchGroupRequests = async () => {
    setIsLoadingGroupRequests(true);
    try {
      const response = await api.get(
        `/api/group-network/get-group-invitations${user.slug}`
      );

      if (response.status === 200 && response.data.result) {
        const { invitations, networks, members } = response.data.result;

        // Transform the data to combine invitations with their corresponding network and member details
        const transformedRequests = invitations.map((invitation) => {
          // Find the corresponding network
          const network =
            networks.find((n) => n.id === invitation.groupNetworkId) || {};

          // Find all members for this network
          const networkMembers = members.filter(
            (m) => m.groupNetworkId === invitation.groupNetworkId
          );

          // Find the inviter school details (if needed)
          const inviterMember = networkMembers.find(
            (m) => m.schoolId === invitation.inviterSchoolId
          );

          return {
            ...invitation,
            id: invitation.id,
            inviterSchoolName: invitation.inviterSchoolName || "Unknown School", // Use the school name from API response
            inviterSchoolLogo: invitation.inviterSchoolLogo,
            inviterSchoolEmail: invitation.inviterSchoolEmail,
            inviterSchoolCity: invitation.inviterSchoolCity,
            inviterSchoolCountry: invitation.inviterSchoolCountry,
            groupNetwork: {
              id: network.id,
              name: network.name,
              description: network.description,
              createdBySchoolId: network.createdBySchoolId,
              isPublic: network.isPublic,
              maxMembers: network.maxMembers,
              status: network.status,
            },
            members: networkMembers,
            memberCount: networkMembers.length,
            inviterRole: inviterMember?.role || "member",
            status: invitation.status || "pending",
            createdAt: invitation.createdAt,
            message: invitation.message,
            expiresAt: invitation.expiresAt,
          };
        });

        setGroupRequests(transformedRequests);
      }
    } catch (error) {
      console.error("Error fetching group requests:", error);
    } finally {
      setIsLoadingGroupRequests(false);
    }
  };

  useEffect(() => {
    getTrustRequests();
    fetchGroupRequests();
  }, []);

  // Filter requests based on search query
  useEffect(() => {
    const filterRequests = (requests) => {
      if (!searchQuery.trim()) return requests;
      
      const query = searchQuery.toLowerCase();
      return requests.filter((request) => {
        // For group requests
        if (request.groupNetwork) {
          return (
            request.groupNetwork.name?.toLowerCase().includes(query) ||
            request.groupNetwork.description?.toLowerCase().includes(query) ||
            request.inviterSchoolName?.toLowerCase().includes(query) ||
            request.message?.toLowerCase().includes(query)
          );
        }
        // For trust requests
        return (
          request.requesterSchool?.name?.toLowerCase().includes(query) ||
          request.targetSchool?.name?.toLowerCase().includes(query) ||
          request.message?.toLowerCase().includes(query) ||
          request.status?.toLowerCase().includes(query)
        );
      });
    };

    setFilteredReceivedRequests(filterRequests(receivedRequests));
    setFilteredCreatedRequests(filterRequests(createdRequests));
    setFilteredGroupRequests(filterRequests(groupRequests));
  }, [searchQuery, receivedRequests, createdRequests, groupRequests]);

  const handleOpenConfig = (request) => {
    setSelectedRequest(request);
    setConfig(
      requestConfigs[request.id] || {
        shareCollection: false,
        shareSpinWheel: false,
        customSettings: null,
      }
    );
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setIsUpdatingConfig(false);
  };

  const handleDialogConfirm = async () => {
    if (selectedRequest) {
      setRequestConfigs((prev) => ({
        ...prev,
        [selectedRequest.id]: config,
      }));

      // Set loading state
      setIsUpdatingConfig(true);

      // Directly approve the request with the configuration
      let result = { success: false };
      
      try {
        // Check if it's a group request by looking at the request structure
        const isGroupRequest = selectedRequest.groupNetwork !== undefined;
        
        if (isGroupRequest) {
          result = await handleApproveGroupRequest(selectedRequest.id, config);
        } else {
          result = await approveTrustRequest(
            selectedRequest.id,
            config
          );
        }

        // Close config dialog only on success
        if (result?.success) {
          setDialogOpen(false);
          setSelectedRequest(null);
        }
      } catch (error) {
        console.error("Error in handleDialogConfirm:", error);
      } finally {
        // Always reset loading state
        setIsUpdatingConfig(false);
      }
    }
  };

  const handleConfigChange = (key) => {
    setConfig((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleApproveGroupRequest = async (requestId, memberConfig = null) => {
    setIsApprovingGroupRequest((prev) => ({ ...prev, [requestId]: true }));
    try {
      const response = await api.put(
        `/api/group-network/invitations/${requestId}${user.slug}`,
        {
          status: "accepted",
          memberConfig: memberConfig || requestConfigs[requestId] || {},
        }
      );

      if (response.status === 200) {
        fetchGroupRequests(); // Refresh the list after approval
        toast.success("Group invitation accepted successfully!");
        return { success: true };
      }
    } catch (error) {
      console.error("Error approving group request:", error);
      toast.error("Failed to approve group invitation");
      return { success: false };
    } finally {
      setIsApprovingGroupRequest((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const handleRejectGroupRequest = async (requestId) => {
    setIsRejectingGroupRequest((prev) => ({ ...prev, [requestId]: true }));
    try {
      const response = await api.put(
        `/api/group-network/invitations/${requestId}${user.slug}`,
        {
          status: "rejected",
        }
      );

      if (response.status === 200) {
        fetchGroupRequests(); // Refresh the list after rejection
        toast.success("Group invitation rejected successfully!");
        return { success: true };
      }
    } catch (error) {
      console.error("Error rejecting group request:", error);
      toast.error("Failed to reject group invitation");
      return { success: false };
    } finally {
      setIsRejectingGroupRequest((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const handleConfirmAction = async () => {
    const { type, requestId, isGroupRequest } = confirmDialog;

    // Set loading state for confirmation dialog
    setConfirmDialog((prev) => ({ ...prev, isLoading: true }));

    let result = { success: false };

    try {
      if (isGroupRequest) {
        if (type === "approve") {
          result = await handleApproveGroupRequest(requestId);
        } else if (type === "reject") {
          result = await handleRejectGroupRequest(requestId);
        }
      } else {
        if (type === "approve") {
          result = await approveTrustRequest(
            requestId,
            requestConfigs[requestId] || {}
          );
        } else if (type === "reject") {
          result = await rejectTrustRequest(requestId);
        }
      }

      // Close config dialog only on success
      if (result?.success) {
        setDialogOpen(false);
        setSelectedRequest(null);
      }
    } catch (error) {
      console.error("Error in handleConfirmAction:", error);
    } finally {
      // Always close confirmation dialog regardless of success or error
      setConfirmDialog({
        open: false,
        type: null,
        requestId: null,
        isGroupRequest: false,
        isLoading: false,
      });
    }
  };

  const handleOpenConfirm = (type, requestId, isGroupRequest = false) => {
    setConfirmDialog({
      open: true,
      type,
      requestId,
      isGroupRequest,
      isLoading: false,
    });
  };

  const handleCloseConfirm = () => {
    setConfirmDialog({
      open: false,
      type: null,
      requestId: null,
      isGroupRequest: false,
      isLoading: false,
    });
  };

  // Container styles are now handled by CSS classes

  if (isLoadingRequests && isLoadingGroupRequests) {
    return (
      <div className="trust-requests-container">
        <div className="loading-container">
          <Loader />
          <div className="loading-text">Loading requests...</div>
        </div>
      </div>
    );
  }

  const displayReceivedRequests = searchQuery.trim() ? filteredReceivedRequests : receivedRequests;
  const displayCreatedRequests = searchQuery.trim() ? filteredCreatedRequests : createdRequests;
  const displayGroupRequests = searchQuery.trim() ? filteredGroupRequests : groupRequests;

  const hasNoResults = displayReceivedRequests.length === 0 &&
    displayCreatedRequests.length === 0 &&
    displayGroupRequests.length === 0;

  return (
    <div className="trust-requests-container">
      {/* Search Bar */}
      <div className="search-container" style={{ marginBottom: "24px" }}>
        <Input
          placeholder={translation.searchRequestsPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          prefix={<SearchOutlined />}
          allowClear
          size="large"
          style={{
            borderRadius: "8px",
            maxWidth: isMobile ? "100%" : "400px"
          }}
        />
      </div>

      {hasNoResults ? (
        <div className="loading-container">
          <InboxOutlined
            style={{ fontSize: "48px", color: "#cbd5e1", marginBottom: "16px" }}
          />
          <div className="loading-text">{translation.noRequestsFound}</div>
        </div>
      ) : (
        <>
          {/* Group Requests Section */}
          <RequestSection
        title={translation.receivedGroupInvitations}
        description={translation.invitations}
        requests={displayGroupRequests}
        onOpenConfig={handleOpenConfig}
        onApprove={(id) => handleOpenConfirm("approve", id, true)}
        onReject={(id) => handleOpenConfirm("reject", id, true)}
        showActions={true}
        isGroupRequest={true}
        isApprovingRequest={isApprovingGroupRequest}
        isRejectingRequest={isRejectingGroupRequest}
        onRequestDeleted={getTrustRequests}
        isMobile={isMobile}
        isMediumScreen={isMediumScreen}
        sectionIcon={<TeamOutlined />}
      />

      {/* Trust Requests Sections */}
      <RequestSection
        title={translation.receivedRequests}
        description={translation.incomingRequests}
        requests={displayReceivedRequests}
        onOpenConfig={handleOpenConfig}
        onApprove={(id) => handleOpenConfirm("approve", id)}
        onReject={(id) => handleOpenConfirm("reject", id)}
        showActions={true}
        onRequestDeleted={getTrustRequests}
        isMobile={isMobile}
        isMediumScreen={isMediumScreen}
        sectionIcon={<InboxOutlined />}
      />

      <RequestSection
        title={translation.sentRequests}
        description={translation.outgoingRequests}
        requests={displayCreatedRequests}
        onOpenConfig={handleOpenConfig}
        onApprove={(id) => handleOpenConfirm("approve", id)}
        onReject={(id) => handleOpenConfirm("reject", id)}
        showActions={false}
        onRequestDeleted={getTrustRequests}
        isMobile={isMobile}
        isMediumScreen={isMediumScreen}
        sectionIcon={<SendOutlined />}
      />
        </>
      )}

      <TrustNetworkConfigDialog
        open={dialogOpen}
        onCancel={handleDialogClose}
        onConfirm={handleDialogConfirm}
        shareCollection={config.shareCollection}
        setShareCollection={() => handleConfigChange("shareCollection")}
        shareSpinWheel={config.shareSpinWheel}
        setShareSpinWheel={() => handleConfigChange("shareSpinWheel")}
        isUpdating={isUpdatingConfig}
      />

      <Dialog
        open={confirmDialog.open}
        onCancel={handleCloseConfirm}
        header={
          confirmDialog.type === "approve"
            ? confirmDialog.isGroupRequest
              ? translation.approveGroupRequestHeader
              : translation.approveTrustRequestHeader
            : confirmDialog.isGroupRequest
            ? translation.rejectGroupRequestHeader
            : translation.rejectTrustRequestHeader
        }
        content={
          confirmDialog.isLoading ? (
            <div
              style={{
                textAlign: "center",
                padding: isMobile ? "15px" : "20px",
              }}
            >
              <Loader />
              <Text
                content={
                  confirmDialog.type === "approve"
                    ? translation.processingApproval
                    : translation.processingRejection
                }
                style={{
                  marginTop: "10px",
                  display: "block",
                  fontSize: isMobile ? "14px" : "16px",
                }}
              />
            </div>
          ) : confirmDialog.type === "approve" ? (
            confirmDialog.isGroupRequest ? (
              translation.approveGroupRequestContent
            ) : (
              translation.approveTrustRequestContent
            )
          ) : confirmDialog.isGroupRequest ? (
            translation.rejectGroupRequestContent
          ) : (
            translation.rejectTrustRequestContent
          )
        }
        confirmButton={{
          content:
            confirmDialog.type === "approve"
              ? translation.yesApprove
              : translation.yesReject,
          primary: confirmDialog.type === "approve",
          danger: confirmDialog.type === "reject",
          disabled: confirmDialog.isLoading,
        }}
        cancelButton={{
          content: translation.cancel,
          disabled: confirmDialog.isLoading,
        }}
        onConfirm={handleConfirmAction}
      />
    </div>
  );
};

export default TrustRequests;
